import {  Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { DxDataGridComponent } from 'devextreme-angular';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import moment from 'moment'
import { HhsgSharedClientApplication } from '../../../models/client-application.model';
import { HhsgSharedClient } from '../../../models/client.model';
import { HhsgSharedFileImport } from '../../../models/file-import.model';
import { HhsgSharedDataImportStatus } from '../../../models/data-import.model';
import { HhsgSharedFileImportDetail } from '../../../models/file-import-details.model';
import { HhsgSharedApplication } from '../../../models/application.model';
import { HhsgSharedCorporation } from '../../../models/corporation.model';
import { Constants } from '../../../utilities/constants.util';
import { HhsgSharedConfigService, HhsgSharedUserAdminService } from '../../../services';

import { HhsgSharedApplicationsService } from '../../../services';
import { HhsgSharedAdminService } from '../../../services/admin.service';
import { HhsgSharedImporterService } from '../../../services/importer.service';
import { HhsgSharedUserProfile, User } from '../../../models';
import { HhsgFileValidationService } from './generic-importer.interface';

@Component({
  selector: 'hhsg-generic-importer',
  templateUrl: './generic-importer.component.html',
  styleUrls: ['./generic-importer.component.scss']
})
export class HhsgSharedGenericImporterComponent implements OnInit, OnDestroy {
  @Input() clientService;
  @Input() fileValidationService: HhsgFileValidationService;
  @ViewChild('fileImportsGrid') fileImportsGrid: DxDataGridComponent;
  @ViewChild('fileImportDetailsGrid') fileImportDetailsGrid: DxDataGridComponent;
  private _unsubscribe$ = new Subject<void>();
  applicationListLoading = false;
  awsBucketRegionConfigured = false;
  browseDisabled = false;
  clientApplications: HhsgSharedClientApplication[];
  clientListLoading = false;
  filteredClients: HhsgSharedClient[];
  fileName = '';
  importDisabled = true;
  importMessage = '';
  selectedApplication: HhsgSharedClientApplication;
  selectedClient: HhsgSharedClient;
  userSelectedFile: File;
  warningMessage = '';
  showDetailsModal: boolean;
  loadingImportList: boolean;
  loadingDetailsList: boolean;
  fileImportsDataStore: CustomStore;
  fileImportDataSource: DataSource;
  lastUpdate: string;
  intervalTimer: any;

  selectedFileImport: HhsgSharedFileImport;
  selectedFileImportDetails: HhsgSharedFileImportDetail[];
  fileImportDetailsDataSource: DataSource;
  fileImportDetailsDataStore: CustomStore;
  allApplications: HhsgSharedApplication[];
  statusList: { id: number; name: string; }[];
  detailsDataSourceStorage = [];
  sortedColumnNumber: number = 0;
  isColumnSorted: boolean = false;
  isPageLoading: boolean = false;
  selectedCorporation: HhsgSharedCorporation = null;
  activeCorporations: HhsgSharedCorporation[];
  corporationListLoading = false;
  applicationAbbrivation: string;
  userProfile: HhsgSharedUserProfile;

  corporationId: number;
  clientId: number;
  fileUploadError: string = null;

  private readonly importStartedMessage = "The import upload has started.";
  private readonly importFailedMessage = "The import upload has failed.";
  private readonly importFinishedMessage = "The import upload has finished.";
  private readonly regionConfigMissingMessage = "The import region has not been configured";


  constructor(
    private elementRef: ElementRef,
    private _applicationService: HhsgSharedApplicationsService,
    private _adminService: HhsgSharedAdminService,
    private _importerService: HhsgSharedImporterService,
    private _configService: HhsgSharedConfigService,
    public _userAdminService: HhsgSharedUserAdminService) {
      this.applicationAbbrivation = _configService.appSettings.applicationAbbreviation;
  }

  async ngOnInit() {  
    this.isPageLoading =true;  
    this.awsBucketRegionConfigured = await this._importerService.hasAwsRegionBeenConfigured();

    if (!this.awsBucketRegionConfigured) {
      this.warningMessage = this.regionConfigMissingMessage;
    }

    await this.loadCorporationList();
    this.allApplications = await this._applicationService.getApplications();
    this.allApplications.sort((a, b) => a.name < b.name ? -1 : 1);
    this.getFileImports();
    this.createStatusList();

    this.intervalTimer = setInterval(async () => {
      let updatedImports = await this._importerService.getUpdatedFileImports(this.lastUpdate);
      this.setLastUpdated();
      if (updatedImports.length > 0) {
        this.fileImportsDataStore.push([{ type: "insert", data: updatedImports }]);
      }
    }, 15000);
    this.isPageLoading =false;  

  }
  ngOnDestroy(){
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  calculateProcessingTime(data: HhsgSharedFileImport) {
    let result = '';
    if (data.processingStartDateTime && data.processingEndDateTime) {
      let start = moment(data.processingStartDateTime);
      let end = moment(data.processingEndDateTime);
      let processingTimeDays = end.diff(start, 'days');
      let processingTimeHours = end.diff(start, 'hours');
      let processingTimeMinutes = end.diff(start, 'minutes');
      let processingTimeSeconds = end.diff(start, 'seconds');
      if (processingTimeDays > 0) {
        result += `${processingTimeDays} day`
        if (processingTimeDays > 1) {
          result += 's';
        }
      }
      if (processingTimeHours > 0) {
        result += ` ${processingTimeHours % 24} hour`
        if (processingTimeHours > 1) {
          result += 's';
        }
      }
      if (processingTimeMinutes > 0) {
        result += ` ${processingTimeMinutes % 60} min`
      }
      if (processingTimeSeconds > 0) {
        result += ` ${processingTimeSeconds % 60} sec`
      }
    }
    return result;
  }

  customizeRecordCount(data: any) {
    return 'Total Records: ' + data.value.toLocaleString();
  }

  browseFile() {
    // Reset the element value to force the change event even when the same file
    // that was picked last time is picked again.
    (<HTMLInputElement>document.getElementById("fileUpload")).value = '';

    // Trigger the file picker.  This is done this way because the file picker contorl is not enabled
    // so we force a click to save us from having to write our own file explorer logic.
    document.getElementById("fileUpload").click();
  }

  getFileImports() {
    this.loadingImportList = true;
    this.setLastUpdated();

    this._importerService
      .getFileImportsDataStoreByApplication(this.applicationAbbrivation)
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(dataStore => {
        this.loadingImportList = false;
        this.fileImportsDataStore = dataStore;
        this.fileImportDataSource = new DataSource({
          store: this.fileImportsDataStore,
          reshapeOnPush: true
        });
      });

  }

  onRowClick(e) {
    this.selectedFileImport = e.data;
    this.loadingDetailsList = true;
    this.showDetailsModal = true;

    this._importerService
      .getFileImportDetailsDataStore(e.key)
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(dataStore => {
        this.loadingDetailsList = false;
        this.fileImportDetailsDataSource = new DataSource({
          store: dataStore
        });
      });

  }

  closeModal() {
    this.showDetailsModal = false;
  }

  detailRowClicked(e) {
    if (e.component.isRowExpanded(e.key)) {
      e.component.collapseRow(e.key);
    } else {
      e.component.expandRow(e.key);
      e.element.getElementsByClassName('dx-row dx-data-row dx-selection')[0].getElementsByTagName('span')[0].innerHTML = 'expand_more';
    }

  }

  detailDataClicked(e) {
    e.stopPropagation();
  }

  getStatusIcon(status: HhsgSharedDataImportStatus) {
    if (status) {
      switch (status) {
        case HhsgSharedDataImportStatus.fileValidationFailed:
          return 'info';
        case HhsgSharedDataImportStatus.error:
          return 'warning';
        case HhsgSharedDataImportStatus.success:
          return 'done';
        case HhsgSharedDataImportStatus.processing:
        case HhsgSharedDataImportStatus.validatingFile:
          return 'sync';
        case HhsgSharedDataImportStatus.pending:
          return 'schedule';
        case HhsgSharedDataImportStatus.waiting:
          return 'history';
        case HhsgSharedDataImportStatus.hiaProcessing:
          return 'redo';
        default:
          return 'sync';
      }
    }
    return '';
  }
  getStatusClass(status: HhsgSharedDataImportStatus) {
    if (status) {
      switch (status) {
        case HhsgSharedDataImportStatus.fileValidationFailed:
          return 'warning-text';
        case HhsgSharedDataImportStatus.error:
          return 'text-yellow';
        case HhsgSharedDataImportStatus.success:
          return 'green-text';
        case HhsgSharedDataImportStatus.processing:
        case HhsgSharedDataImportStatus.validatingFile:
          return '';
        case HhsgSharedDataImportStatus.pending:
          return '';
        case HhsgSharedDataImportStatus.waiting:
          return '';
        case HhsgSharedDataImportStatus.hiaProcessing:
          return '';
        default:
          return '';
      }
    }
    return '';
  }

  async importFile() {
    if (this.importDisabled) {
      return;
    }

    this.browseDisabled = true;
    this.importDisabled = true;
    this.importMessage = this.importStartedMessage;

    await this.uploadFileToServer(this.userSelectedFile);

    this.browseDisabled = false;
  }

  get isLoading() {
    return this.clientListLoading || this.applicationListLoading || this.corporationListLoading;
  }

  private async loadApplicationList() {
    if (!this.selectedClient) {
      return;
    }

    this.applicationListLoading = true;
    this.clientApplications = await this._applicationService.getClientApplicationsForImporter(this.selectedClient.id, this.applicationAbbrivation);
    this.clientApplications = this.clientApplications.filter(x => x.applicationAbbreviation == Constants.HuronAcceleratorsAbbreviation || (x.fileImportTask != null && x.fileImportTask != ""))
    this.applicationListLoading = false;
  }

  private async loadClientList() {
    if(!this.selectedCorporation){
      return;
    }
    this.clientListLoading = true;
    let clients = await this._adminService.getAllActiveClientsForImporter(this.applicationAbbrivation);
    this.filteredClients = clients.filter(c => c.accountNumber && c.corporationId == this.selectedCorporation.id);
    let userClient = clients.filter((item) => {
      return item.id == this.clientId;
    });
    if(userClient.length > 0){
      this.selectedClient = userClient[0];
      this.onClientSelected();
    }
    this.clientListLoading = false;
  }

  onApplicationSelected() {
    this.updateImportDisabled();
  }

  async onClientSelected() {
    this.selectedApplication = null;
    await this.loadApplicationList();
    this.updateImportDisabled();
  }

   private createStatusList() {
    this.statusList = [{
      id: HhsgSharedDataImportStatus.pending,
      name: 'Pending'
    }, {
      id: HhsgSharedDataImportStatus.validatingFile,
      name: 'Validating File'
    }, {
      id: HhsgSharedDataImportStatus.fileValidationFailed,
      name: 'Validation Failed'
    }, {
      id: HhsgSharedDataImportStatus.processing,
      name: 'App Processing'
    }, {
      id: HhsgSharedDataImportStatus.error,
      name: 'Error'
    }, {
      id: HhsgSharedDataImportStatus.success,
      name: 'Success'
    }];
  }

  getStatusText(id: HhsgSharedDataImportStatus): string {
    if (this.statusList) {
      let status = this.statusList.find(s => s.id == id);
      return status ? status.name : '';
    }
    return '';
  }

  private resetSelectedFile() {
    this.fileName = '';
    this.userSelectedFile = null;
  }

  acceptedFileExtensions() {
    const supportedExtensions = this.fileValidationService.supportedFileExtensions;
    return supportedExtensions?.length > 0 ? supportedExtensions.map(x => `.${x}`).join(', ') : null;
  }

  selectFile(event: any) {
    this.resetSelectedFile();
    this.importMessage = '';

    if (!event || !event.target || !event.target.files || !event.target.files.length || event.target.files.length == 0) {
      return;
    }

    this.userSelectedFile = event.target.files[0];
    this.fileName = this.userSelectedFile.name;
    this.updateImportDisabled();
  }

  private setLastUpdated() {
    this.lastUpdate = moment().utc().format();
  }

  private updateImportDisabled() {
    if(!this.fileValidationService.isFileValid(this.userSelectedFile)) {
      this.fileUploadError = this.fileValidationService.fileUploadError;
    }

    this.importDisabled = !this.fileValidationService.isFileValid(this.userSelectedFile) || !this.awsBucketRegionConfigured || !this.userSelectedFile 
                          || !this.selectedCorporation|| !this.selectedClient|| !this.selectedApplication;
  }

  private async uploadFileToServer(file: File | Blob) {
    this.isPageLoading =true;  

    if (!file) {
      return;
    }

    let formData = new FormData();
    formData.append(this.fileName, file);

    await this._importerService.saveFileToS3(formData, this.selectedClient.id, this.selectedApplication.applicationId)
      .then(result => {
        if (result) {
          this.importMessage = this.importFinishedMessage;
          this.fileImportsDataStore.push([{ type: "insert", data: result }]);
        }
        else {
          this.importMessage = this.importFailedMessage;
        }
      })
      .catch(error => {
        this.importMessage = this.importFailedMessage;
      });

    this.resetSelectedFile();

    this.updateImportDisabled();
    this.isPageLoading =false;  

  }

  onOptionChanged(events) {
    if (events.fullName.includes("sort")) {
      this.isColumnSorted = true;
      this.elementRef.nativeElement.getElementsByClassName("dx-header-row")[0].setAttribute("style", "background-color: #dedede;");
      this.sortedColumnNumber = events.fullName.charAt(8);
      this.changeSortedColumnBackgroundColor();
    }
  }

  clearPreviousColumnColor() {
    var dataRows = this.elementRef.nativeElement.getElementsByClassName("cell-highlighted");
    while (dataRows.length > 0)
      dataRows[0].classList.remove("cell-highlighted");
  }

  changeSortedColumnBackgroundColor() {
    this.clearPreviousColumnColor();
    var dataRows = this.elementRef.nativeElement.getElementsByClassName("dx-row");
    for (let index = 0; index < dataRows.length; index++) {
      if (dataRows[index].children[this.sortedColumnNumber]) {
        dataRows[index].children[this.sortedColumnNumber].classList.add("cell-highlighted");
      }
    }
  }

  setTableHeaderStyle() {
    this.elementRef.nativeElement.getElementsByClassName("dx-header-row")[0].children[7].setAttribute("style", "color: black;");

    this.changeSortedColumnBackgroundColor();
  }
  getImportButtonStatus(): string {
    if (this.fileName.trim() == '' || this.importDisabled)
      return "btn-secondary btn-primary-disabled";
    else
      return "btn-secondary";
  }

  isImportButtonDisabled(): boolean {
    if (this.importDisabled || this.fileName.trim() == '') {
      return true;
    }
    else
      return false;

  }

  setModalTableHeaderStyle() {
    this.elementRef.nativeElement.getElementsByClassName("dx-header-row")[1].setAttribute("style", "color: black;");
  }

  private async loadCorporationList() {
    this.corporationListLoading = true;
    this.activeCorporations = await this._adminService.getFileImportTaskEnabledCorporationsForUserByApplication(this.applicationAbbrivation);
    this.corporationListLoading = false;
    let clientId = Number(JSON.parse(sessionStorage.getItem("hacid")));
    if(!clientId){
      let userProfile = JSON.parse(sessionStorage.getItem("hsgUserProfile"));
      if (userProfile) {
        clientId = userProfile.clientId;
      }
    }
    this.clientService.getClientByClientIdForImporter(clientId)
    .pipe(takeUntil(this._unsubscribe$)).subscribe((clientDetails) => {
      this.corporationId = clientDetails.corporationId;
      this.clientId = clientDetails.clientId;
      let userCorporation = this.activeCorporations.filter((item) => {
        return item.id == this.corporationId;
      });
      if(userCorporation.length > 0){
        this.selectedCorporation = userCorporation[0];
        this.onCorporationSelected();
      }
    });
  }

  async onCorporationSelected() {
    this.selectedClient = null;
    this.selectedApplication = null;
    await this.loadClientList();
    this.updateImportDisabled();
  }
}
